import { createContext, PropsWithChildren } from 'react';
import { linkBridge } from '@webview-bridge/web';

import type { AppBridge as AppBridgeType } from '../types/appbridge';

import { usePushNotification } from '../modules/usePushNotification';
import { useStoreReview } from '../modules/useStoreReview';
import { useCapra } from '../modules/useCapra';

import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import useContextWrapper from './hook';

export interface ApiAdapter {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post(url: string, data?: any): Promise<unknown>;
}

type AppBridgeContextType = {
  bridge: typeof bridge;
  brand: 'ac' | 'cp';
  isRunningInNativeApp: boolean;
  shouldShowAppExclusiveContent: boolean;
  notifications: ReturnType<typeof usePushNotification>;
  storeReview: ReturnType<typeof useStoreReview>;
  capra: ReturnType<typeof useCapra>;
};

export interface WebBridgeProviderProps extends PropsWithChildren {
  apiInstance?: ApiAdapter;
  brand: 'ac' | 'cp';
}

export const bridge = linkBridge<AppBridgeType>({
  throwOnError: true,
  timeout: 60000, // 60 seconds
});

export type LooseBridge = typeof bridge.loose;
export type BridgeType = typeof bridge;

if (bridge.isWebViewBridgeAvailable) {
  window.isNativeApp = bridge.isWebViewBridgeAvailable || false;
  window.bridge = bridge.loose;
}

const bypassAppExclusiveContent = Boolean(getCookie('bypass-app-exclusive-content')?.includes('true'));

export const WebBridgeContext = createContext<AppBridgeContextType | null>(null);

export const useWebBridgeContext = () =>
  useContextWrapper(WebBridgeContext, {
    name: 'WebBridge',
    provider: 'WebBridgeProvider',
  });

export const WebBridgeProvider = ({ children, apiInstance, brand }: WebBridgeProviderProps) => {
  const isRunningInNativeApp = bridge.isWebViewBridgeAvailable;
  const shouldShowAppExclusiveContent = bridge.isWebViewBridgeAvailable || bypassAppExclusiveContent;
  const notifications = usePushNotification(bridge, brand, apiInstance);
  const storeReview = useStoreReview(bridge);
  const capra = useCapra(bridge);

  return (
    <WebBridgeContext.Provider
      value={{
        bridge,
        brand,
        isRunningInNativeApp,
        shouldShowAppExclusiveContent,
        notifications,
        storeReview,
        capra,
      }}
    >
      {children}
    </WebBridgeContext.Provider>
  );
};
